import { extractLinks } from '@app/utils'
import { LISTING_SLUGS, PROJECT_SLUGS } from '@app/utils/regional/constants'
import { getAllowedDomains } from '@app/utils/url'

const KNOWN_DOMAIN_PATTERNS = ['properstar', 'sodichan']

const allowedEnvironmentDomains = getAllowedDomains()

const buildSlugSet = (lookup: Record<string, string[]>): Set<string> => {
  const set = new Set<string>()

  Object.values(lookup).forEach((slugs) => {
    slugs.forEach((slug) => {
      set.add(slug.toLowerCase())
    })
  })

  return set
}

const listingSlugSet = buildSlugSet(LISTING_SLUGS)
const projectSlugSet = buildSlugSet(PROJECT_SLUGS)

const hasProtocol = (value: string) => /^[a-z][a-z0-9+.-]*:/i.test(value)

const toAbsoluteUrl = (link: string): string | null => {
  if (!link) {
    return null
  }

  const trimmed = link.trim()
  if (!trimmed) {
    return null
  }

  if (hasProtocol(trimmed)) {
    return trimmed
  }

  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`
  }

  return `https://${trimmed}`
}

const isKnownHost = (host: string): boolean =>
  KNOWN_DOMAIN_PATTERNS.some((pattern) => host.includes(pattern))

const extractListingIdFromUrl = (url: URL): number | null => {
  const host = url.hostname.toLowerCase()
  if (!isKnownHost(host)) {
    return null
  }

  if (!allowedEnvironmentDomains.has(host)) {
    return null
  }

  const segments = url.pathname
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)

  if (segments.length < 2) {
    return null
  }

  const idSegment = decodeURIComponent(segments[segments.length - 1])
  if (!/^\d+$/.test(idSegment)) {
    return null
  }

  const slugSegment = decodeURIComponent(
    segments[segments.length - 2]
  ).toLowerCase()

  if (!listingSlugSet.has(slugSegment) && !projectSlugSet.has(slugSegment)) {
    return null
  }

  const listingId = Number.parseInt(idSegment, 10)
  return Number.isFinite(listingId) ? listingId : null
}

export const getListingIdFromLink = (link: string): number | null => {
  const absolute = toAbsoluteUrl(link)
  if (!absolute) {
    return null
  }

  try {
    const url = new URL(absolute)
    return extractListingIdFromUrl(url)
  } catch {
    return null
  }
}

export const getListingIdsFromLinks = (links: string[]): number[] => {
  const uniqueIds = new Set<number>()

  links.forEach((link) => {
    const listingId = getListingIdFromLink(link)
    if (listingId) {
      uniqueIds.add(listingId)
    }
  })

  return [...uniqueIds]
}

export const getListingIdsFromText = (text?: string): number[] => {
  if (!text) {
    return []
  }

  const links = extractLinks(text)
  return getListingIdsFromLinks(links)
}

export const splitListingLinks = (links: string[]) => {
  const listingLinks: { link: string; listingId: number }[] = []
  const otherLinks: string[] = []

  links.forEach((link) => {
    const listingId = getListingIdFromLink(link)
    if (listingId) {
      listingLinks.push({ link, listingId })
    } else {
      otherLinks.push(link)
    }
  })

  return {
    listingLinks,
    otherLinks,
  }
}
